import ASSETS from '@generio-ai/library-legacy/constants/assets'
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import { File as AssetFile } from '@generio-ai/library-legacy/stores/_api'
import { Task } from '@/classes/task.js'
import { TaskFetch } from '@/classes/taskfetch.js'
import { readFilesWithParameters } from '@/utils/file.js'
import { AGENT_WAITING, agentError, normalizeAgentStatus } from '@/utils/agent.js'

const DEFAULT_AGENT = 'openai_6.1_sol'

export default {
    commitAgentInput({ dispatch, rootGetters }, { input }) {
        return new Task(async (resolve, reject, progress, app) => {
            try {
                progress('Sending request')
                const info = input.custom.agent
                if (!input.uploads && input.localFiles?.length) {
                    const { fileData, fileParameters } = await readFilesWithParameters(input.localFiles, ASSETS.TYPES.IMAGE)
                    input.uploads = fileData.map((data, index) => ({ data, parameters: fileParameters[index] }))
                    input.localAttachments = input.localAttachments.map((file, index) => {
                        if (file.preview?.startsWith('blob:')) URL.revokeObjectURL(file.preview)
                        return { ...file, preview: fileData[index] }
                    })
                }
                const uploads = input.uploads?.length ? input.uploads : [null]
                input.assets ||= []
                const context = info.contextAssetId ? rootGetters['assets/getAsset'](info.contextAssetId) : null
                const parent = context ? { getAssetIds: () => [context.id] } : null

                // Resume after a partial image upload without committing those images twice.
                for (let index = input.assets.length; index < uploads.length; index++) {
                    const upload = uploads[index]
                    const additional = input.createAdditional(parent)
                    additional.custom = { ...input.custom, agent: { ...info, imageIndex: index, name: input.localFiles?.[index]?.name } }
                    const response = await dispatch('assets/create', {
                        app,
                        files: upload ? [new AssetFile(ASSETS.FILES.KEY.DEFAULT, upload.data, null, upload.parameters)] : null,
                        thumbnail: !!upload,
                        additional
                    }, { root: true })
                    if (!response?.assets?.[0]) throw new Error('Saving the input did not return an asset.')
                    const asset = rootGetters['assets/getAsset'](response.assets[0].id) || response.assets[0]
                    input.assets.push(asset)
                }
                for (const asset of input.assets.filter(asset => asset.type === ASSETS.TYPES.IMAGE)) {
                    const retrieved = await dispatch('assets/fetchFile', { asset }, { root: true })
                    if (!retrieved) throw new Error('A reference image could not be prepared. Please try again.')
                }
                // Persist the request identity before contacting the agent.
                await dispatch('persistInput', { input })
                resolve({ assets: input.assets })
            }
            catch (error) { reject(error) }
        })
    },

    async persistInput({ dispatch }, { input }) {
        await Promise.all((input.assets || []).map(asset => dispatch('assets/updateAdditional', {
            asset,
            additional: {
                ...asset.additional,
                custom: { ...asset.additional?.custom, agent: { ...asset.additional?.custom?.agent, ...input.custom.agent } }
            }
        }, { root: true })))
    },

    fetchAgent({ dispatch, commit, rootGetters }, { input, responseCard, resume = false, agent = DEFAULT_AGENT }) {
        return new TaskFetch(responseCard.component, CATEGORY.MODEL, async (resolve, reject, progress, app) => {
            try {
                let model = responseCard.assets?.[0]
                if (!resume) {
                    input.custom.agent.activeRequest.state = 'requesting'
                    await dispatch('persistInput', { input })
                    progress(AGENT_WAITING)
                    const references = input.assets.filter(asset => asset.type !== ASSETS.TYPES.PRIMITIVE)
                        .map(asset => ({ id: asset.id, fileKey: ASSETS.FILES.KEY.DEFAULT }))
                    const contextId = input.custom.agent.contextAssetId
                    if (contextId) references.unshift({ id: contextId, fileKey: ASSETS.FILES.KEY.DEFAULT })
                    let response
                    try {
                        // The library's assets/generateModelFromAsset name is overridden by its model actions.
                        response = await dispatch('api/agentsRunPost', {
                            app,
                            agent: agent || DEFAULT_AGENT,
                            assets: references,
                            prompt: input.prompt.user,
                            thumbnail: true,
                            additional: responseCard.createAdditional(input)
                        }, { root: true })
                    }
                    catch (error) {
                        if (!error.status || error.status >= 500 || error.status === 408) {
                            throw agentError('The agent request could not be confirmed. Check its status before sending another request.', true)
                        }
                        throw error
                    }
                    if (!response?.assets?.[0]) throw agentError('The agent returned no model asset. Check the request status.', true)
                    commit('assets/UPDATE_OR_ADD', response.assets, { root: true })
                    model = rootGetters['assets/getAsset'](response.assets[0].id) || response.assets[0]
                    responseCard.assets = [model]
                    progress(AGENT_WAITING, null, [model])
                }
                await dispatch('awaitRetrievedAgentModel', { model, onProgress: progress })
                resolve({ assets: [model] })
            }
            catch (error) { reject(error) }
        })
    },

    async refreshAgentModel({ dispatch, commit }, { model, final = false }) {
        // Bypass fetchFile's retrieved-data cache so each update can replace the model.
        const response = await dispatch('api/assetsAssetFilesFileGet', { id: model.id }, { root: true })
        const file = response?.files?.[ASSETS.FILES.KEY.DEFAULT]
        if (!file?.data) {
            if (final) throw new Error('The final model is not available yet.')
            return false
        }
        commit('assets/UPDATE_FILES', {
            id: model.id,
            files: { [ASSETS.FILES.KEY.DEFAULT]: { ...file, state: ASSETS.FILES.STATE.RETRIEVED } }
        }, { root: true })
        return true
    },

    async awaitRetrievedAgentModel({ dispatch }, {
        model,
        onProgress = () => {},
        delay = 2000,
        waitMaximum = 15 * 60_000
    }) {
        if (!model) throw agentError('The model asset could not be located.', true)
        const started = Date.now()
        let lastStatus = null
        let refreshNeeded = false
        while (Date.now() - started < waitMaximum) {
            let status
            try {
                status = normalizeAgentStatus(await dispatch('api/assetsAssetFilesFileStatusGet', { id: model.id }, { root: true }))
            }
            catch {
                onProgress('Connection interrupted. Reconnecting', null, [model])
                await new Promise(resolve => setTimeout(resolve, delay))
                continue
            }
            if (status.state === 'error') throw agentError(status.message || 'Model generation failed. Please try again.')
            if (status.state === 'ready') {
                onProgress('Loading your model', null, [model])
                try {
                    await dispatch('refreshAgentModel', { model, final: true })
                    return
                }
                catch {
                    await new Promise(resolve => setTimeout(resolve, delay))
                    continue
                }
            }
            onProgress(status.message, null, [model])
            if (status.message !== lastStatus) refreshNeeded = true
            lastStatus = status.message
            if (refreshNeeded) {
                try {
                    refreshNeeded = !await dispatch('refreshAgentModel', { model })
                }
                catch { /* A preview can be unavailable until the next agent update. */ }
            }
            await new Promise(resolve => setTimeout(resolve, delay))
        }
        throw agentError('The agent is taking longer than expected. Check its status to continue waiting.', true)
    }
}
